import { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { ChevronRight, ArrowDown } from "lucide-react";
import { ROUTE_PATHS } from "@/lib/index";

/* ─── ścieżki grafik ──────────────────────────────────────── */
const IMG = {
  tree:       "/images/idea_tree_eternity_20260415_111235.png",
  hourglass:  "/images/idea_hourglass_legacy_20260415_111238.png",
  path:       "/images/idea_cemetery_path_20260415_111241.png",
  feather:    "/images/idea_feather_soul_20260415_111235.png",
  moon:       "/images/idea_acceptance_moon_20260415_111239.png",
  book:       "/images/idea_book_memory_20260415_111237.png",
};

/* ─── helper: sekcja wjeżdżająca przy scroll ─────────────── */
function FadeIn({ children, delay = 0, className = "" }: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── separator złota ────────────────────────────────────── */
function GoldDivider({ className = "" }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center gap-4 ${className}`}>
      <div className="h-px flex-1 max-w-24" style={{ background: 'linear-gradient(to right, transparent, rgba(212,175,55,0.4))' }} />
      <div className="w-1 h-1 rounded-full" style={{ background: 'rgba(212,175,55,0.6)' }} />
      <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(212,175,55,0.8)' }} />
      <div className="w-1 h-1 rounded-full" style={{ background: 'rgba(212,175,55,0.6)' }} />
      <div className="h-px flex-1 max-w-24" style={{ background: 'linear-gradient(to left, transparent, rgba(212,175,55,0.4))' }} />
    </div>
  );
}

/* ─── cytat ──────────────────────────────────────────────── */
function Quote({ text, author }: { text: string; author?: string }) {
  return (
    <div className="relative px-8 py-6">
      <div className="absolute left-0 top-0 bottom-0 w-px" style={{ background: 'linear-gradient(to bottom, transparent, rgba(212,175,55,0.5), transparent)' }} />
      <p className="text-lg md:text-xl leading-relaxed italic"
        style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(212,175,55,0.75)' }}>
        „{text}"
      </p>
      {author && (
        <p className="mt-3 text-sm" style={{ color: 'rgba(140,155,175,0.6)' }}>— {author}</p>
      )}
    </div>
  );
}

/* ─── sekcja hero z paralaksą ────────────────────────────── */
function HeroSection() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <div ref={ref} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Tło z paralaksą */}
      <motion.div className="absolute inset-0 z-0" style={{ y }}>
        <img src={IMG.tree} alt="" className="w-full h-full object-cover"
          style={{ opacity: 0.28, filter: 'brightness(0.7) saturate(0.8)' }} />
        <div className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, oklch(0.07 0.015 235 / 0.4) 0%, transparent 30%, transparent 60%, oklch(0.07 0.015 235) 100%)' }} />
      </motion.div>

      {/* Treść hero */}
      <motion.div style={{ opacity }} className="relative z-10 text-center px-4 max-w-3xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }}
          className="text-xs uppercase tracking-[0.4em] mb-8"
          style={{ color: 'rgba(212,175,55,0.55)' }}>
          e-grób · filozofia
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, delay: 0.5 }}
          className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
          style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(235,228,208,0.92)', textShadow: '0 2px 40px rgba(0,0,0,0.8)' }}>
          Śmierć jest<br />
          <span style={{ color: 'rgba(212,175,55,0.78)' }}>częścią życia</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.8 }}
          className="text-lg md:text-xl leading-relaxed mb-12"
          style={{ color: 'rgba(175,190,210,0.75)' }}>
          Rozmawiamy o niej szeptem. Odsuwamy od siebie myśl o niej.<br className="hidden md:block" />
          A jednak jest jedyną pewną rzeczą w każdym życiu.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.2 }}
          className="flex flex-col items-center gap-3">
          <span className="text-sm" style={{ color: 'rgba(140,155,175,0.5)' }}>Przewiń, aby dowiedzieć się więcej</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
            <ArrowDown className="w-5 h-5" style={{ color: 'rgba(212,175,55,0.4)' }} />
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}

/* ─── sekcja narracyjna: obraz + tekst ───────────────────── */
function NarrativeBlock({
  img, alt, imgLeft = true, tag, title, body, quote, quoteAuthor,
}: {
  img: string; alt: string; imgLeft?: boolean;
  tag: string; title: string; body: React.ReactNode;
  quote?: string; quoteAuthor?: string;
}) {
  return (
    <FadeIn className="grid md:grid-cols-2 gap-10 md:gap-16 items-center py-16 md:py-24">
      {/* Grafika */}
      <div className={`relative ${imgLeft ? 'md:order-1' : 'md:order-2'}`}>
        <div className="relative overflow-hidden rounded-2xl"
          style={{ boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,175,55,0.08)' }}>
          <img src={img} alt={alt} className="w-full aspect-square object-cover"
            style={{ filter: 'brightness(0.82) saturate(0.85)' }} />
          {/* złoty overlay na krawędzi */}
          <div className="absolute inset-0 rounded-2xl"
            style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.04) 0%, transparent 60%)', border: '1px solid rgba(212,175,55,0.12)' }} />
        </div>
        {/* Dekoracyjny element za obrazem */}
        <div className="absolute -z-10 rounded-2xl"
          style={{
            inset: '-12px',
            background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.06) 0%, transparent 70%)',
          }} />
      </div>

      {/* Tekst */}
      <div className={`${imgLeft ? 'md:order-2' : 'md:order-1'}`}>
        <p className="text-xs uppercase tracking-[0.35em] mb-4" style={{ color: 'rgba(212,175,55,0.55)' }}>
          {tag}
        </p>
        <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-snug"
          style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(228,220,198,0.92)' }}>
          {title}
        </h2>
        <div className="space-y-4 mb-8" style={{ color: 'rgba(165,180,200,0.75)', lineHeight: '1.85', fontSize: '0.95rem' }}>
          {body}
        </div>
        {quote && <Quote text={quote} author={quoteAuthor} />}
      </div>
    </FadeIn>
  );
}

/* ─── sekcja statystyk ────────────────────────────────────── */
function StatsSection() {
  const stats = [
    { value: "74%", label: "Polaków nigdy nie rozmawiało z bliskimi o śmierci", src: "CBOS 2023" },
    { value: "91%", label: "ludzi odczuwa lęk przed śmiercią, ale tylko 12% go przepracowało", src: "APA Research" },
    { value: "3×", label: "rzadziej chorujemy na depresję, jeśli świadomie oswoimy temat śmierci", src: "WHO Mental Health Report" },
    { value: "∞", label: "chwil, które warte są zachowania dla tych, którzy przyjdą po nas", src: "e-grób" },
  ];

  return (
    <FadeIn className="py-16 md:py-24">
      <div className="text-center mb-12">
        <p className="text-xs uppercase tracking-[0.35em] mb-3" style={{ color: 'rgba(212,175,55,0.55)' }}>liczby mówią same za siebie</p>
        <h2 className="text-3xl md:text-4xl font-bold"
          style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(228,220,198,0.9)' }}>
          Milczenie ma swoją cenę
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {stats.map((s, i) => (
          <FadeIn key={i} delay={i * 0.1}>
            <div className="text-center p-6 rounded-2xl"
              style={{ background: 'oklch(0.10 0.025 215)', border: '1px solid oklch(0.16 0.03 220)' }}>
              <div className="text-4xl md:text-5xl font-bold mb-3"
                style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(212,175,55,0.8)' }}>
                {s.value}
              </div>
              <p className="text-sm leading-snug mb-3" style={{ color: 'rgba(155,170,190,0.75)' }}>
                {s.label}
              </p>
              <p className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(100,115,135,0.5)' }}>
                {s.src}
              </p>
            </div>
          </FadeIn>
        ))}
      </div>
    </FadeIn>
  );
}

/* ─── sekcja kroków — jak to działa ─────────────────────── */
function ManifestoSection() {
  const items = [
    {
      num: "01",
      title: "Lęk jest naturalny",
      text: "Od zarania dziejów śmierć budziła w ludziach trwogę. To jeden z najgłębszych ludzkich instynktów — strach przed niebytem, przed utratą siebie. Nie jesteś sam w tym uczuciu. Każdy je nosi.",
    },
    {
      num: "02",
      title: "Milczenie go pogłębia",
      text: "Współczesna kultura wypiera śmierć. Nie ma jej w codziennych rozmowach, w szkołach, w mediach. Poza kościołem — gdzie rytuał zastępuje rozmowę — nikt nam nie pomaga zmierzyć się z przemijaniem.",
    },
    {
      num: "03",
      title: "Rozmowa leczy",
      text: "Psychologia i filozofia od dekad potwierdzają: im bardziej świadomie oswajamy myśl o śmierci, tym pełniej żyjemy. Stoicy nazywali to \"memento mori\" — pamiętaj, że umrzesz. Nie jako przekleństwo, lecz jako dar.",
    },
    {
      num: "04",
      title: "Zostawiasz ślad",
      text: "e-grób nie jest miejscem smutku. To miejsce, gdzie możesz zostawić kawałek siebie — słowa, zdjęcia, historię. Dla tych, którzy przyjdą po tobie. Dla tych, którzy chcą pamiętać.",
    },
  ];

  return (
    <FadeIn className="py-16 md:py-24">
      <div className="text-center mb-16">
        <p className="text-xs uppercase tracking-[0.35em] mb-3" style={{ color: 'rgba(212,175,55,0.55)' }}>manifest</p>
        <h2 className="text-3xl md:text-4xl font-bold"
          style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(228,220,198,0.9)' }}>
          Dlaczego e-grób istnieje
        </h2>
      </div>
      <div className="space-y-0">
        {items.map((item, i) => (
          <FadeIn key={i} delay={i * 0.12}>
            <div className="grid md:grid-cols-[80px_1fr] gap-6 py-8 border-b"
              style={{ borderColor: 'oklch(0.14 0.025 220)' }}>
              <div className="text-5xl font-bold leading-none" style={{
                fontFamily: "'Playfair Display', serif",
                color: 'rgba(212,175,55,0.18)',
                letterSpacing: '-0.02em',
              }}>
                {item.num}
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3"
                  style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(220,212,190,0.88)' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'rgba(155,170,190,0.72)', lineHeight: '1.85', fontSize: '0.94rem' }}>
                  {item.text}
                </p>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
    </FadeIn>
  );
}

/* ─── pełnoekranowy cytat z tłem ─────────────────────────── */
function FullWidthQuote({ img, text, author }: { img: string; text: string; author: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <div ref={ref} className="relative overflow-hidden py-32 my-8">
      <motion.div className="absolute inset-0 z-0" style={{ y }}>
        <img src={img} alt="" className="w-full h-full object-cover"
          style={{ opacity: 0.22, filter: 'brightness(0.6) saturate(0.7)' }} />
      </motion.div>
      <div className="absolute inset-0 z-0"
        style={{ background: 'linear-gradient(135deg, oklch(0.07 0.02 235 / 0.88), oklch(0.09 0.03 220 / 0.75))' }} />
      <div className="relative z-10 container mx-auto px-4 max-w-2xl text-center">
        <div className="text-6xl mb-6" style={{ color: 'rgba(212,175,55,0.3)', fontFamily: 'Georgia, serif' }}>"</div>
        <p className="text-2xl md:text-3xl leading-relaxed italic mb-6"
          style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(228,218,190,0.88)' }}>
          {text}
        </p>
        <p className="text-sm uppercase tracking-widest" style={{ color: 'rgba(212,175,55,0.55)' }}>
          — {author}
        </p>
      </div>
    </div>
  );
}

/* ─── CTA końcowe ─────────────────────────────────────────── */
function CtaSection() {
  return (
    <FadeIn className="py-24 text-center">
      <GoldDivider className="mb-16" />
      <p className="text-xs uppercase tracking-[0.35em] mb-4" style={{ color: 'rgba(212,175,55,0.5)' }}>
        zacznij teraz
      </p>
      <h2 className="text-4xl md:text-5xl font-bold mb-6"
        style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(228,220,198,0.92)' }}>
        Zostaw coś po sobie.
      </h2>
      <p className="text-lg mb-12 max-w-xl mx-auto" style={{ color: 'rgba(155,170,190,0.7)', lineHeight: '1.9' }}>
        Twój e-grób to nie miejsce śmierci — to miejsce, w którym żyjesz dalej.<br />
        Dla kogoś, kto będzie chciał Cię pamiętać.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <Link to={ROUTE_PATHS.CMENTARZ}>
          <button className="px-8 py-4 rounded-xl font-medium text-base flex items-center gap-2 transition-all duration-300 hover:brightness-110 hover:scale-105"
            style={{ background: 'rgba(212,175,55,0.82)', color: 'rgba(8,9,18,0.95)', boxShadow: '0 4px 24px rgba(212,175,55,0.2)' }}>
            Zarezerwuj swoje miejsce
            <ChevronRight className="w-5 h-5" />
          </button>
        </Link>
        <Link to={ROUTE_PATHS.HOME}>
          <button className="px-8 py-4 rounded-xl font-medium text-base transition-all duration-300 hover:bg-white/5"
            style={{ border: '1px solid rgba(212,175,55,0.25)', color: 'rgba(185,198,215,0.8)' }}>
            Wróć na stronę główną
          </button>
        </Link>
      </div>
    </FadeIn>
  );
}

/* ════════════════════════════════════════════════════════════
   Główny komponent strony
═══════════════════════════════════════════════════════════ */
export default function Idea() {
  return (
    <Layout>
      <div style={{ background: 'oklch(0.075 0.015 232)' }}>

        {/* ── HERO ── */}
        <HeroSection />

        {/* ── INTRO: jedno zdanie, które zatrzymuje ── */}
        <div className="container mx-auto px-4 max-w-3xl py-20 text-center">
          <FadeIn>
            <GoldDivider className="mb-14" />
            <p className="text-2xl md:text-3xl leading-relaxed"
              style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(200,212,228,0.78)', lineHeight: '1.8' }}>
              Żyjemy tak, jakby śmierć nas nie dotyczyła.<br />
              A potem — kiedy przychodzi — nie mamy słów.
            </p>
            <GoldDivider className="mt-14" />
          </FadeIn>
        </div>

        {/* ── NARRACJE Z GRAFIKAMI ── */}
        <div className="container mx-auto px-4 max-w-5xl">

          <NarrativeBlock
            img={IMG.moon}
            alt="Sylwetka człowieka pod pełnią księżyca"
            imgLeft={false}
            tag="lęk pierwotny"
            title="Najstarszy strach człowieka"
            body={
              <>
                <p>Lęk przed śmiercią jest wbudowany w nas głębiej niż cokolwiek innego. Ewolucja przez miliony lat nagradzała tych, którzy uciekali przed śmiercią, nie tych, którzy przy niej siadali i rozmawiali.</p>
                <p>To dlatego samo słowo „śmierć" wywołuje napięcie w ciele. To dlatego omijamy temat. To dlatego mówimy „odszedł", „nie żyje", „straciliśmy" — wszystko, byle nie powiedzieć wprost.</p>
                <p>Ten lęk jest <em>normalny</em>. Ale nie musi nami rządzić.</p>
              </>
            }
            quote="Kto nauczył się umierać, ten oduczył się być niewolnikiem."
            quoteAuthor="Michel de Montaigne"
          />

          <GoldDivider className="my-4" />

          <NarrativeBlock
            img={IMG.path}
            alt="Ścieżka przez cmentarz o zmierzchu"
            imgLeft={true}
            tag="kultura milczenia"
            title="Poza kościołem — cisza"
            body={
              <>
                <p>W Polsce śmierć ma swoje miejsce w rytuale religijnym. Pogrzeb, msza, modlitwa. Ale poza tymi ścianami — cisza. Szkoła nie uczy jak rozmawiać o umieraniu. Rodziny omijają temat. Media go wypierają.</p>
                <p>Efekt? Kiedy naprawdę musimy zmierzyć się ze stratą — albo z własną skońszonością — jesteśmy sami, bez narzędzi, bez języka, bez przestrzeni.</p>
                <p>Terapeuci mówią, że to jeden z głównych powodów, dla których żałoba bywa tak druzgocąca. Nie sama strata — ale brak gotowości na nią.</p>
              </>
            }
            quote="Nie boimy się śmierci. Boimy się niegotowości na nią."
            quoteAuthor="Irvin D. Yalom, psychiatra"
          />
        </div>

        {/* ── FULLWIDTH QUOTE 1 ── */}
        <FullWidthQuote
          img={IMG.feather}
          text="Pamiętaj, że umrzesz — nie po to, by się bać, lecz by w pełni żyć."
          author="Marek Aureliusz, Rozmyślania"
        />

        {/* ── STATYSTYKI ── */}
        <div className="container mx-auto px-4 max-w-5xl">
          <StatsSection />

          <GoldDivider className="my-4" />

          <NarrativeBlock
            img={IMG.hourglass}
            alt="Klepsydra przechodząca z rąk do rąk"
            imgLeft={false}
            tag="dziedzictwo"
            title="Co zostaje po tobie?"
            body={
              <>
                <p>Każde życie to historia. Imiona, daty, twarze na zdjęciach — to za mało, żeby opowiedzieć, kim był człowiek. Czego się bał. Co kochał. Czym się cieszył w codzienności.</p>
                <p>e-grób to przestrzeń, gdzie możesz zostawić więcej niż kamień z datami. Słowa, które chcesz, żeby ktoś kiedyś przeczytał. Zdjęcia, które mają kontekst tylko w Twojej głowie. Historię, która inaczej zniknie.</p>
                <p>Bo dziedzictwo to nie majątek. To ślad, który pozwala komuś powiedzieć: <em>„Znałam tę osobę. Naprawdę."</em></p>
              </>
            }
            quote="Człowiek umiera dwa razy — raz gdy oddaje ostatni oddech, i drugi raz gdy ostatnia osoba wymawia jego imię."
            quoteAuthor="powiedzenie bantu (Afryka Zachodnia)"
          />
        </div>

        {/* ── FULLWIDTH QUOTE 2 ── */}
        <FullWidthQuote
          img={IMG.book}
          text="Historia każdego człowieka jest warta opowiedzenia. I warta zapamiętania."
          author="e-grób, 2026"
        />

        {/* ── MANIFEST ── */}
        <div className="container mx-auto px-4 max-w-3xl">
          <ManifestoSection />
        </div>

        {/* ── OSTATNIA GRAFIKA: DRZEWO ── */}
        <FadeIn className="py-12">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="relative overflow-hidden rounded-3xl"
              style={{ boxShadow: '0 40px 100px rgba(0,0,0,0.7)' }}>
              <img src={IMG.tree} alt="Drzewo wieczności" className="w-full aspect-[2.5/1] object-cover"
                style={{ filter: 'brightness(0.75) saturate(0.8)', objectPosition: 'center 40%' }} />
              <div className="absolute inset-0"
                style={{ background: 'linear-gradient(to right, rgba(7,8,18,0.8) 0%, rgba(7,8,18,0.2) 50%, rgba(7,8,18,0.7) 100%)' }} />
              <div className="absolute inset-0 flex items-center justify-start px-10 md:px-20">
                <div className="max-w-sm">
                  <p className="text-xs uppercase tracking-[0.35em] mb-4" style={{ color: 'rgba(212,175,55,0.6)' }}>
                    wieczność
                  </p>
                  <h3 className="text-3xl md:text-4xl font-bold mb-4 leading-snug"
                    style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(235,225,200,0.92)' }}>
                    Każde drzewo<br />zaczyna się<br />od ziarna.
                  </h3>
                  <p style={{ color: 'rgba(165,178,195,0.72)', lineHeight: '1.8', fontSize: '0.9rem' }}>
                    Twój e-grób to ziarno pamięci. Małe, ale trwałe. Dla tych, którzy przyjdą po tobie.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>

        {/* ── CTA ── */}
        <div className="container mx-auto px-4 max-w-3xl">
          <CtaSection />
        </div>

      </div>
    </Layout>
  );
}
